import { InputHTMLAttributes, ReactNode, Ref, useCallback, useRef } from 'react';

import { FormattedInput } from '@flipster/react/design-utils/formatted-input';
import { useMergeRef } from '@flipster/react/misc/ref/use-merge-ref';
import { useControllableState } from '@flipster/react/misc/state/use-controllable-state';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';

import {
  deformatSignedBasisSecondary,
  formatSignedBasisSecondaryDisplay,
  normalizeInlineRoiPnlSecondary,
  normalizeModalSecondaryByExpectedSign,
  type SecondarySign,
} from '../shared/tpsl/tpsl-basis-field/signed-secondary';

export interface NumberInputWithSuffixProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  ref?: Ref<HTMLInputElement>;
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  precision: number;
  max?: string;
  suffix?: ReactNode;
  /**
   * Show `+` for positive non-zero values.
   * Implies signed input when true.
   */
  withPlus?: boolean;
  /**
   * Inline SL ROI/PnL: always force-negative on every change (`10` → `-10`).
   * No leading `+`. Implies signed input.
   */
  forceNegative?: boolean;
  /**
   * Modal Change/ROI/PnL: force this sign after full clear (keep opposite while editing).
   * Implies signed input. Prefer with `withPlus={false}`.
   */
  expectedSign?: SecondarySign;
}

// field-sizing 속성을 이용하면 코드를 훨씬 단순하게 만들 수 있지만
// 브라우저 지원범위가 매우 협소해서 안보이는 div로 위치를 잡아주는 방식으로 구현함.
// - https://developer.chrome.com/docs/css-ui/css-field-sizing
// - https://caniuse.com/?search=field-sizing
export const NumberInputWithSuffix = ({
  ref,
  className,
  precision,
  max,
  onChange,
  value,
  defaultValue,
  suffix,
  withPlus = false,
  forceNegative = false,
  expectedSign,
  onFocus,
  onBlur,
  ...props
}: NumberInputWithSuffixProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRef(inputRef, ref);
  const isSigned = withPlus || forceNegative || expectedSign != null;
  const [innerValue, setInnerValue] = useControllableState({
    value,
    defaultValue,
    onChange(nextValue) {
      // forceNegative / expectedSign already applied in deformatter so cursor math sees normalized value.
      if (checkOverMax(nextValue, max)) {
        onChange?.(numberFormatter(max, { withComma: false, precision }));
        inputRef.current?.setSelectionRange(-1, -1);
        return;
      }
      onChange?.(nextValue);
    },
  });
  const formatter = useCallback(
    (val: string) => {
      let normalized = val;
      if (forceNegative) {
        normalized = normalizeInlineRoiPnlSecondary('sl', val);
      } else if (expectedSign != null) {
        normalized = normalizeModalSecondaryByExpectedSign(expectedSign, val, innerValue || '');
      }
      return isSigned
        ? formatSignedBasisSecondaryDisplay(normalized, precision, { withPlus })
        : inputFormatter(normalized, { withComma: true, precision });
    },
    [precision, isSigned, withPlus, forceNegative, expectedSign, innerValue],
  );
  const deformatter = useCallback(
    (val: string) => {
      const base = isSigned ? deformatSignedBasisSecondary(val) : val.replace(/[^\d.]/g, '');
      if (forceNegative) return normalizeInlineRoiPnlSecondary('sl', base);
      if (expectedSign != null) return normalizeModalSecondaryByExpectedSign(expectedSign, base, innerValue || '');
      return base;
    },
    [isSigned, forceNegative, expectedSign, innerValue],
  );
  const formattedValue = formatter(innerValue || '');

  const handleFocus: NonNullable<NumberInputWithSuffixProps['onFocus']> = (event) => {
    // Modal expected-negative: insert `-` on click-to-type (placeholder stays sign-free).
    if (expectedSign === 'negative') {
      const current = innerValue || '';
      if (current === '') {
        setInnerValue('-');
        requestAnimationFrame(() => {
          inputRef.current?.setSelectionRange(1, 1);
        });
      }
    }
    onFocus?.(event);
  };

  const handleBlur: NonNullable<NumberInputWithSuffixProps['onBlur']> = (event) => {
    // Drop lone `-` so empty placeholder shows again.
    if (expectedSign === 'negative' && (innerValue === '-' || innerValue === '')) {
      if (innerValue === '-') setInnerValue('');
    }
    onBlur?.(event);
  };

  return (
    <div
      className={cx(
        className,
        hstack({
          h: 'inherit',
          pos: 'relative',
          flexBasis: 0,
          flexGrow: 1,
          '&:focus-within .clear': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }),
      )}
    >
      <FormattedInput
        ref={mergedRef}
        className={css({
          font: 'inherit',
          pos: 'absolute',
          p: 'inherit',
          pr: 0,
          minW: 0,
          w: '100%',
          h: '100%',
          color: 'inherit',
          zIndex: 1,
        })}
        value={innerValue}
        onChange={setInnerValue}
        formatter={formatter}
        deformatter={deformatter}
        maxLength={12}
        inputMode='numeric'
        autoCapitalize='off'
        autoComplete='off'
        autoCorrect='off'
        spellCheck='false'
        data-lpignore='true'
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
      <div
        className={css({
          font: 'inherit',
          letterSpacing: 'normal',
          opacity: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        })}
      >
        {formattedValue}
      </div>
      {suffix}
    </div>
  );
};

function checkOverMax(val?: string, max?: string) {
  return val != null && max != null && Num(val).isGreaterThan(max);
}
